import { fn } from '@ember/helper';
import type Owner from '@ember/owner';

import { PopularLazyLoadSelector } from 'webapp/utils/common';
import type { DuplicateCleanupConfig } from 'webapp/utils/html';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer from 'webapp/modifiers/visible-check-container';
import CommonAddButtonComponent from 'webapp/components/common/add-button';
import CommonBatchButtonComponent from 'webapp/components/common/batch-button';
import CommonBatchTableHeaderComponent from 'webapp/components/common/batch-table-header';
import CommonListLazyLoadComponent from 'webapp/components/common/list/lazy-load';
import CommonListLoaderComponent from 'webapp/components/common/list/loader';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import GoalsTableRowComponent from 'webapp/components/goals/table-row';
import GoalsTeamBaseComponent, { type GoalsTeamArgs } from 'webapp/components/goals/team/base';
import SectionWithTogglerComponent from 'webapp/components/section-with-toggler';
import TableAddNewItemComponent from 'webapp/components/table/add-new-item';

import type { Goal } from 'webapp/models-custom';
import FilterSortGoalTeamInList from 'webapp/models-custom/filter-sort/goal-teams-in-list';

export default class GoalTeamTableComponent extends GoalsTeamBaseComponent {
	filterSort: FilterSortGoalTeamInList;

	constructor(owner: Owner, args: GoalsTeamArgs) {
		super(owner, args);

		const filterSort = new FilterSortGoalTeamInList({
			selectors: PopularLazyLoadSelector.table,
			getAllItems: () => {
				return this.model;
			},
			onOpenView: (viewId: string) => {
				this.args.openView(viewId);
			},
			afterFilter: () => {
				this.doVisibleCheck();
			},
		});

		this.filterSort = filterSort;
	}

	get duplicateCleanupConfig(): DuplicateCleanupConfig {
		return {
			sectionSelectors: ['.favorites-section', '.non-favorites-section'],
			itemSelector: 'tr.table-row[data-id]',
			shouldKeepInSection: (itemId: string, sectionSelector: string): boolean => {
				const goal = this.mmGoalsService.goalForSpaceModels.find(
					(goal: Goal) => goal.id === itemId,
				);
				if (!goal) {
					return false;
				}
				return (
					(goal.favorite && sectionSelector === '.favorites-section') ||
					(!goal.favorite && sectionSelector === '.non-favorites-section')
				);
			},
		};
	}
	<template>
		<div class="bottom-padding">
			<FilterLineProxyComponent @model={{this.filterSort}} />

			<FilterNoResultsComponent @model={{this.filterSort}} />

			{{#each this.teamModels as |team|}}
				<SectionWithTogglerComponent
					@isOpen={{this.getSectionToggleState team.idForSure}}
					@title={{team.name}}
					@toggle={{fn this.toggleSection team.idForSure}}
				>
					{{! Favorites section }}
					{{#if (this.hasFavoriteGoalsForTeam team.idForSure)}}
						<CommonListLazyLoadComponent
							@filterSort={{this.filterSort}}
							@filterExtraFn={{this.favoriteGoalsFilterForTeamFn team.idForSure}}
							@initialRowCount={{15}}
							@loadMoreRowCount={{10}}
							@animationProperty="id"
							@autoLoaded={{true}}
							@bottomDistance={{400}}
							@checkHTMLNodeValidity={{this.cleanupDuplicateNodes}}
							as |visibleItems exporter|
						>
							<table
								class="goals-list flexible-table bigtable has-checkbox has-avatar favorites-section
									{{if this.filterSort.noResults 'hide'}}"
								{{! don't add dynamic classes bcos of visible-check-container }}
								{{didInsert this.setupFavoritesView}}
								{{visibleCheckContainer}}
								{{didUpdate this.triggerContentChange visibleItems}}
							>
								<colgroup>
									<col class="cc" />
									<col class="h0" />
									<col width="25%" />
									<col width="25%" />
									<col />
									<col />
									<col />
									<col class="button-ct" />
								</colgroup>

								<thead>
									<tr>
										<CommonBatchTableHeaderComponent
											@batchSelect={{this.batchSelect}}
										/>
										<th class="h0"></th>
										<th>Title</th>
										<th>Progress</th>
										<th>Timeframe</th>
										<th class="prio-low">Status</th>
										<th class="prio-low">Owner</th>
										<th class="button-ct"></th>
									</tr>
								</thead>

								{{! don"t add dynamic classes on tbody bcos of sortable }}
								<tbody>
									{{#each visibleItems as |result|}}
										<GoalsTableRowComponent
											@model={{result}}
											@batchSelect={{this.batchSelect}}
											@hideSort={{exporter.hideSort}}
											@hideDragHandle={{true}}
										/>
									{{/each}}
								</tbody>
							</table>

							{{#unless exporter.areAllItemsLoaded}}
								<CommonListLoaderComponent />
							{{/unless}}
						</CommonListLazyLoadComponent>
					{{/if}}

					{{! Non-favorites section }}
					<CommonListLazyLoadComponent
						@filterSort={{this.filterSort}}
						@filterExtraFn={{this.nonFavoriteGoalsFilterForTeamFn team.idForSure}}
						@initialRowCount={{15}}
						@loadMoreRowCount={{10}}
						@animationProperty="id"
						@autoLoaded={{true}}
						@bottomDistance={{400}}
						@checkHTMLNodeValidity={{this.cleanupDuplicateNodes}}
						as |visibleItems exporter|
					>
						<table
							class="goals-list flexible-table bigtable has-checkbox has-avatar non-favorites-section
								{{if this.filterSort.noResults 'hide'}}"
							{{! don't add dynamic classes bcos of visible-check-container }}
							{{didInsert this.setupNonFavoritesView}}
							{{visibleCheckContainer}}
							{{didUpdate this.triggerContentChange visibleItems}}
						>
							<colgroup>
								<col class="cc" />
								<col class="h0" />
								<col width="25%" />
								<col width="25%" />
								<col />
								<col />
								<col />
								<col class="button-ct" />
							</colgroup>

							{{#unless (this.hasFavoriteGoalsForTeam team.idForSure)}}
								<thead>
									<tr>
										<CommonBatchTableHeaderComponent
											@batchSelect={{this.batchSelect}}
										/>
										<th class="h0"></th>
										<th>Title</th>
										<th>Progress</th>
										<th>Timeframe</th>
										<th class="prio-low">Status</th>
										<th class="prio-low">Owner</th>
										<th class="button-ct"></th>
									</tr>
								</thead>
							{{/unless}}

							{{! don"t add dynamic classes on tbody bcos of sortable }}
							<tbody>
								{{#each visibleItems as |result|}}
									<GoalsTableRowComponent
										@model={{result}}
										@batchSelect={{this.batchSelect}}
										@hideSort={{exporter.hideSort}}
										@hideDragHandle={{true}}
									/>
								{{/each}}
							</tbody>
						</table>

						<TableAddNewItemComponent
							@title={{this.addButtonText}}
							@addNewItem={{fn @onAdd team.idForSure}}
							@model={{this.filterSort}}
						/>

						{{#unless exporter.areAllItemsLoaded}}
							<CommonListLoaderComponent />
						{{/unless}}
					</CommonListLazyLoadComponent>
				</SectionWithTogglerComponent>
			{{/each}}
		</div>

		{{#if this.addButtonContainerNode}}
			{{#in-element this.addButtonContainerNode}}
				<CommonAddButtonComponent @text={{this.addButtonText}} @callTrigger={{@onAdd}} />
			{{/in-element}}
		{{/if}}

		{{#if this.batchButtonContainerNode}}
			{{#in-element this.batchButtonContainerNode}}
				<CommonBatchButtonComponent @model={{this.batchSelect}} />
			{{/in-element}}
		{{/if}}
	</template>
}
